import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CalendarEvent } from '../../services/definitions.service';
import { NotiBottomSheetComponent, NotiSheetData, NotiType } from '../../noti-bottom-sheet/noti-bottom-sheet.component';

export interface CalendarDayEventsDialogData {
  date: Date;
  events: CalendarEvent[];
}

export interface CalendarDayEventsDialogResult {
  action: 'create' | 'edit' | 'delete' | 'close';
  event?: CalendarEvent;
}

@Component({
  selector: 'app-calendar-day-events-dialog',
  templateUrl: './calendar-day-events-dialog.component.html',
  styleUrls: ['./calendar-day-events-dialog.component.css'],
})
export class CalendarDayEventsDialogComponent {
  private readonly dayFormatter = new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
  });
  private readonly dateOnlyFormatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  private readonly fullDateFormatter = new Intl.DateTimeFormat('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  private readonly shortDateFormatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  constructor(
    public dialogRef: MatDialogRef<CalendarDayEventsDialogComponent>,
    private bottomSheet: MatBottomSheet,
    @Inject(MAT_DIALOG_DATA) public data: CalendarDayEventsDialogData
  ) {}

  formatFullDate(date: Date): string {
    return this.fullDateFormatter.format(new Date(date));
  }

  formatDay(date: Date): string {
    return this.dayFormatter.format(new Date(date));
  }

  formatDateOnly(date: Date): string {
    return this.dateOnlyFormatter.format(new Date(date));
  }

  getTypeColor(type: string): string {
    switch (type) {
      case 'meeting':
        return '#1a73e8';
      case 'task':
        return '#34a853';
      case 'reminder':
        return '#fbbc04';
      default:
        return '#5f6368';
    }
  }

  formatShortDate(date: Date): string {
    return this.shortDateFormatter.format(this.asDate(date));
  }

  asDate(value: any): Date {
    if (value?.toDate) {
      return value.toDate();
    }

    return new Date(value);
  }

  onClose(): void {
    this.dialogRef.close({ action: 'close' } as CalendarDayEventsDialogResult);
  }

  onCreate(): void {
    this.dialogRef.close({ action: 'create' } as CalendarDayEventsDialogResult);
  }

  onEdit(event: CalendarEvent): void {
    this.dialogRef.close({
      action: 'edit',
      event,
    } as CalendarDayEventsDialogResult);
  }

  onDelete(event: CalendarEvent): void {
    const sheetRef = this.bottomSheet.open(NotiBottomSheetComponent, {
      data: new NotiSheetData(
        `Möchtest du das Event "${event?.name || 'Unbenannt'}" wirklich löschen?`,
        NotiType.Warn,
        0,
        true,
        true,
        'Löschen',
        'Abbrechen'
      ),
      panelClass: 'noti-bottom-sheet-info',
      disableClose: false,
    });

    sheetRef.afterDismissed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      this.dialogRef.close({
        action: 'delete',
        event,
      } as CalendarDayEventsDialogResult);
    });
  }
}
